import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import * as Router from "react-router-dom";
import { Table, Sort } from "component/shared/table";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { Lead } from "model/lead";
import { urls } from "routes/urls";
import { styles } from "./style";
import * as env from "shared/env";
import { LeadAvatar } from "component/shared/lead-avatar";
import { LeadStatus } from "type/lead-status";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { updateLead } from "redux/slice/leads";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { Select } from "component/shared/select";
import {
	setLeadsPageFilters,
	updateLeadsPageSort,
	updateLeadsPagePageSize,
	updateLeadsPagePage,
	updateLeadsPageColumns,
} from "redux/slice/leads-page";
import {
	getLeadsPageFilters,
	getLeadsPageSort,
	getLeadsPagePageSize,
	getLeadsPagePage,
	getLeadsPageColumns,
	getActiveLeads,
} from "redux/selector";
import { Tag } from "./tag";
import { Bookmark } from "component/shared/bookmark";
import {
	numberedDateFormatter,
	shortDateFormatter,
} from "shared/date-formatter";
import {
	requestCancelLeadImport,
	clearStaleLeadImportProgress,
	getRecentLeadImport,
	getLeadImportProgress,
	clearLeadImportProgress,
	clearRecentLeadImport,
	resetCancelLeadImport,
	RecentLeadImport,
	LeadImportProgress,
} from "shared/lead-import-recent";
import { LeadColumnType } from "type/lead-column";
import { Type } from "./type";
import { User } from "model/user";
import { AddLeadDialogV2 } from "component/shared/add-lead-dialog-v2";
import { DeleteLeadDialog } from "./deleteDialog";
import { LeadImportModal } from "./lead-import-modal";
import { StopImportConfirmDialog } from "component/shared/stop-import-confirm-dialog";
import { formatPhoneNumber } from "shared/phone-number-formatter";
import { BulkActionTypes } from "type/bulk-action-types";
import { BulkAddTagsDialog } from "./bulk-actions/dialogs/bulk-add-tags-dialog";
import { BulkRemoveTagsDialog } from "./bulk-actions/dialogs/bulk-remove-tags-dialog";
import { BulkCampaignDialog } from "./bulk-actions/dialogs/bulk-campaign-dialog";
import { BulkPipelineStageDialog } from "./bulk-actions/dialogs/bulk-pipeline-stage-dialog";
import { BulkExportCSVDialog } from "./bulk-actions/dialogs/bulk-export-csv-dialog";
import { BulkAssignToAgentDialog } from "./bulk-actions/dialogs/bulk-assign-to-agent";
import { BulkAddToMarketDialog } from "./bulk-actions/dialogs/bulk-add-to-market-dialog";
import {
	withMediaQuery,
	WithMediaQuery,
} from "component/shared/with-media-query";
import { Ranking } from "component/shared/ranking";
import { BulkDeleteDialog } from "./bulk-delete/dialogs/bulk-delete-dialog";
import { calculateLeadRankSize } from "shared/lead-rank-size";
import moment from "moment";
import { formatPriceInput } from "shared/price-input-formatter";
import { LeadReadiness } from "type/lead-readiness";
import { YesNoType } from "type/boolean-choice-type";
import MuiAlert from "@mui/material/Alert";
import classNames from "classnames";
import { LeadNavigationLocationState } from "shared/lead-navigation";
import { getLeadsPageColumnTypes, leadSortColumns } from "shared/lead-list";
import { hasSearchAgent } from "shared/search-agent-permission";
import { SparklesIcon } from "component/shared/sparkles-icon";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	const user = ownProps.user;
	const columnTypes = getLeadsPageColumnTypes(user, getLeadsPageColumns(state));
	return {
		allActiveLeads: getActiveLeads(state),
		filters: getLeadsPageFilters(state),
		sort: getLeadsPageSort(state),
		pageSize: getLeadsPagePageSize(state),
		page: getLeadsPagePage(state),
		columnTypes,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateLead,
			setLeadsPageFilters,
			updateLeadsPageSort,
			updateLeadsPagePageSize,
			updateLeadsPagePage,
			updateLeadsPageColumns,
		},
		dispatch
	);

interface OwnProps {
	user: User;
	leads: Lead[];
	onLeadCreated: (leadId: number) => void;
	termChanged: boolean;
	showLeadFilterDialog: () => void;
	filterCount: number;
	term: string;
	onTermChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
	loading?: boolean;
	initialTerm: string;
	focusLeadId?: number;
}

type Props = OwnProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles> &
	WithMediaQuery;

const LeadTableComponent: React.FC<Props> = (props) => {
	const {
		user,
		leads,
		allActiveLeads,
		onLeadCreated,
		sort,
		pageSize,
		page,
		columnTypes,
		filters,
		mediaQuery: isMobile,
		updateLeadsPageColumns,
		setLeadsPageFilters,
		updateLeadsPageSort,
		updateLeadsPagePageSize,
		updateLeadsPagePage,
		updateLead,
		classes = {},
		termChanged,
		showLeadFilterDialog,
		filterCount,
		term,
		initialTerm,
		onTermChange,
		loading,
		focusLeadId,
	} = props;

	const history = Router.useHistory();
	const scrolledToFocusLeadRef = useRef(false);

	const [deleteLeadDialogIsOpen, setDeleteLeadDialogIsOpen] = useState(false);
	const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
	const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
	const [addLeadDialogIsOpen, setAddLeadDialogIsOpen] = useState(false);
	const [importLeadsDialogIsOpen, setImportLeadsDialogIsOpen] = useState(false);
	const [selectedLeadIds, setSelectedLeadIds] = useState<Set<number>>(new Set());
	const [columnsAnchorEl, setColumnsAnchorEl] = useState<null | HTMLElement>(null);
	const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedBulkActionId, setSelectedBulkActionId] = useState<number | undefined>(undefined);
	const [recentImport, setRecentImport] = useState<RecentLeadImport | null>(() =>
		getRecentLeadImport(user.clientId)
	);
	const [importProgress, setImportProgress] = useState<LeadImportProgress | null>(() =>
		getLeadImportProgress(user.clientId)
	);
	const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
	const [isStopping, setIsStopping] = useState(false);

	React.useEffect(() => {
		if (termChanged) {
			updateLeadsPagePage(0);
		}
	}, [termChanged, updateLeadsPagePage]);

	useEffect(() => {
		setRecentImport(getRecentLeadImport(user.clientId));
	}, [user.clientId, leads]);

	useEffect(() => {
		// Clear stale progress on mount (e.g., after page refresh during import)
		// This uses a per-page-load session id to detect refreshes
		clearStaleLeadImportProgress(user.clientId);

		// Update state after clearing stale data
		setRecentImport(getRecentLeadImport(user.clientId));
		setImportProgress(getLeadImportProgress(user.clientId));

		const handleProgressUpdate = () => {
			const progress = getLeadImportProgress(user.clientId);
			setImportProgress(progress);
			// Reset stopping state when import is no longer in progress
			if (!progress || !progress.inProgress) {
				setIsStopping(false);
			}
		};

		window.addEventListener("lead-import-progress", handleProgressUpdate);
		return () => {
			window.removeEventListener("lead-import-progress", handleProgressUpdate);
		};
	}, [user.clientId]);


	const recentImportEmailSet = useMemo(() => {
		if (!recentImport || recentImport.dismissed) {
			return new Set<string>();
		}
		return new Set(recentImport.emails.map((email) => email.toLowerCase()));
	}, [recentImport]);

	const isRecentlyImported = useCallback(
		(lead: Lead) => recentImportEmailSet.has(lead.email.toLowerCase()),
		[recentImportEmailSet]
	);

	const sortByRecentFirst = useCallback(
		(a: Lead, b: Lead) => {
			const aRecent = isRecentlyImported(a) ? 1 : 0;
			const bRecent = isRecentlyImported(b) ? 1 : 0;
			if (aRecent !== bRecent) return bRecent - aRecent;
			return b.createdOn.valueOf() - a.createdOn.valueOf();
		},
		[isRecentlyImported]
	);

	const sortedLeads = useMemo(() => {
		if (!recentImport || recentImport.dismissed) return leads;
		return [...leads].sort(sortByRecentFirst);
	}, [leads, recentImport, sortByRecentFirst]);

	const sortedAllActiveLeads = useMemo(() => {
		if (!recentImport || recentImport.dismissed) return allActiveLeads;
		return [...allActiveLeads].sort(sortByRecentFirst);
	}, [allActiveLeads, recentImport, sortByRecentFirst]);

	const handleLeadClick = useCallback(
		(event: React.MouseEvent, lead: Lead) => {
			// let modified clicks (new tab/window) through; those open without a navigation set
			if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
				return;
			}
			event.preventDefault();
			// so the browser back button also keeps the search and brings this lead into view
			const returnState: LeadNavigationLocationState = {
				leadsPageReturn: { leadId: lead.id },
			};
			history.replace({ ...history.location, state: returnState });
			const state: LeadNavigationLocationState = { fromLeadList: true, leadListTerm: term };
			history.push(urls.lead(lead), state);
		},
		[history, term]
	);

	useEffect(() => {
		if (!focusLeadId || !classes.focusedLeadRow || scrolledToFocusLeadRef.current) {
			return;
		}
		const row = document.getElementsByClassName(classes.focusedLeadRow)[0];
		if (row) {
			row.scrollIntoView({ block: "center" });
			scrolledToFocusLeadRef.current = true;
		}
	}, [focusLeadId, sortedLeads, page, classes.focusedLeadRow]);

	const handleDismissRecentImport = useCallback(() => {
		clearRecentLeadImport(user.clientId);
		setRecentImport(getRecentLeadImport(user.clientId));
	}, [user.clientId]);

	const handleStopImport = useCallback(() => {
		setStopConfirmOpen(true);
	}, []);

	const handleConfirmStop = useCallback(() => {
		requestCancelLeadImport(user.clientId);
		setStopConfirmOpen(false);
		setIsStopping(true);
	}, [user.clientId]);

	const handleCancelStop = useCallback(() => {
		setStopConfirmOpen(false);
	}, []);

	const isProgressBannerVisible =
		Boolean(importProgress?.inProgress && importProgress.total > 0);
	const isRecentBannerVisible =
		Boolean(recentImport && !recentImport.dismissed && recentImport.emails.length > 0);
	const recentImportCount = recentImport?.totalImported ?? recentImport?.emails.length ?? 0;
	const bannerOffset = isProgressBannerVisible || isRecentBannerVisible ? 79 : 0;
	const isImporting = Boolean(importProgress?.inProgress);


	const columnOptions = useMemo(() => {
		const results: LeadColumnType[] = [];
		results.push(LeadColumnType.AVATAR);
		results.push(LeadColumnType.LABEL);
		results.push(LeadColumnType.CREATED_ON);
		if (user.permissions.extendedLeadFields) {
			results.push(LeadColumnType.TIMELINE);
			results.push(LeadColumnType.MORTGAGE_PRE_APPROVAL);
			results.push(LeadColumnType.WORKING_WITH_ANOTHER_AGENT);
			results.push(LeadColumnType.REFERRAL_REQUESTED);
			results.push(LeadColumnType.TRANSACTION_PRICE);
			results.push(LeadColumnType.READINESS);
		}
		if (user.permissions.leadLastActive) {
			results.push(LeadColumnType.LAST_ACTIVE_ON);
		}
		if (hasSearchAgent(user)) {
			results.push(LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON);
		}
		results.push(LeadColumnType.STATUS);
		results.push(LeadColumnType.SUBSCRIBED);
		if (user.permissions.crmPipeline) {
			results.push(LeadColumnType.PIPELINE);
		}
		if (user.permissions.crmTags) {
			results.push(LeadColumnType.TYPE);
		}
		if (user.permissions.crm && env.leadRanking) {
			results.push(LeadColumnType.RANKING);
		}
		results.push(LeadColumnType.SOURCE);
		if (user.permissions.crmTags) {
			results.push(LeadColumnType.BOOKMARKED);
		}
		if (user.permissions.broker && user.permissions.showTeamFeatures) {
			results.push(LeadColumnType.AGENT);
		}
		if (user.permissions.crmTags) {
			results.push(LeadColumnType.TAGS);
		}
		return results;
	}, [user]);

	const leadRankData = useMemo(
		() => calculateLeadRankSize([...allActiveLeads]),
		[allActiveLeads]
	);

	const handleAddLeadDialogOpen = useCallback(
		() => setAddLeadDialogIsOpen(true),
		[]
	);
	const handleAddLeadDialogClose = useCallback(
		() => setAddLeadDialogIsOpen(false),
		[]
	);
	const handleImportLeadsClick = useCallback(() => {
		if (recentImport && !recentImport.dismissed) {
			clearRecentLeadImport(user.clientId);
			setRecentImport(getRecentLeadImport(user.clientId));
		}
		setImportLeadsDialogIsOpen(true);
	}, [recentImport, user.clientId]);
	const handleImportLeadsDialogClose = useCallback(() => {
		setImportLeadsDialogIsOpen(false);
	}, []);
	const handleShowDeleteLeadDialog = useCallback((lead: Lead) => {
		setLeadToDelete(lead);
		setDeleteLeadDialogIsOpen(true);
	}, []);
	const handleHideDeleteLeadDialog = useCallback(() => {
		setLeadToDelete(null);
		setDeleteLeadDialogIsOpen(false);
	}, []);
	const handleDeleteLead = useCallback(() => {
		if (leadToDelete) {
			updateLead({
				lead: {
					...leadToDelete,
					status: LeadStatus.DELETED,
				},
			});
		}
		handleHideDeleteLeadDialog();
	}, [leadToDelete, updateLead, handleHideDeleteLeadDialog]);

	const handleSortChange = useCallback(
		(sort: Sort) => {
			updateLeadsPageSort(sort);
		},
		[updateLeadsPageSort]
	);

	const handlePageSizeChange = useCallback(
		(pageSize: number) => {
			updateLeadsPagePageSize(pageSize);
		},
		[updateLeadsPagePageSize]
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateLeadsPagePage(page);
		},
		[updateLeadsPagePage]
	);

	const handleSelect = useCallback((leads: Lead[]) => {
		setSelectedLeadIds((prev) => {
			const next = new Set(prev);
			leads.forEach((lead) => next.add(lead.id));
			return next;
		});
	}, []);

	const handleUnselect = useCallback((leads: Lead[]) => {
		setSelectedLeadIds((prev) => {
			const next = new Set(prev);
			leads.forEach((lead) => next.delete(lead.id));
			return next;
		});
	}, []);

	const handleColumnsButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		setColumnsAnchorEl(event.currentTarget);
	}, []);

	const handleColumnsMenuClose = useCallback(() => {
		setColumnsAnchorEl(null);
	}, []);

	const handleColumnToggle = useCallback(
		(type: LeadColumnType) => {
			const newColumns = columnTypes.includes(type)
				? columnTypes.filter((t) => t !== type)
				: [...columnTypes, type];
			updateLeadsPageColumns(newColumns);
		},
		[columnTypes, updateLeadsPageColumns]
	);

	const canShowBulkActions = user.permissions.crmBulkActions;

	const bulkActions = useMemo(() => {
		if (!canShowBulkActions) return [];
		return BulkActionTypes.values().filter(
			(a) => !a.needBrokerPermission || (user.permissions.broker && user.admin)
		);
	}, [canShowBulkActions, user.permissions.broker, user.admin]);

	const handleBulkActionsButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		setBulkActionsAnchorEl(event.currentTarget);
	}, []);

	const handleBulkActionsMenuClose = useCallback(() => {
		setBulkActionsAnchorEl(null);
	}, []);

	const handleBulkActionSelect = useCallback((actionId: number) => {
		setSelectedBulkActionId(actionId);
		setBulkActionsAnchorEl(null);
	}, []);

	const handleBulkActionDialogClose = useCallback(() => {
		setSelectedBulkActionId(undefined);
	}, []);

	const handleStatusChange = useCallback(
		(values: string[]) => {
			setLeadsPageFilters({
				...filters,
				status: values,
			});
		},
		[setLeadsPageFilters, filters]
	);

	return (
		<>
			<AddLeadDialogV2
				open={addLeadDialogIsOpen}
				onClose={handleAddLeadDialogClose}
				onLeadCreated={onLeadCreated}
				user={user}
			/>
			<LeadImportModal
				open={importLeadsDialogIsOpen}
				onClose={handleImportLeadsDialogClose}
				user={user}
			/>
			<DeleteLeadDialog
				deleteLeadDialogIsOpen={deleteLeadDialogIsOpen}
				hideDeleteLeadDialog={handleHideDeleteLeadDialog}
				deleteLead={handleDeleteLead}
			/>
			{selectedBulkActionId === BulkActionTypes.ADD_TAGS.id && (
				<BulkAddTagsDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{selectedBulkActionId === BulkActionTypes.REMOVE_TAGS.id && (
				<BulkRemoveTagsDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{selectedBulkActionId === BulkActionTypes.UPDATE_STAGE.id && (
				<BulkPipelineStageDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{selectedBulkActionId === BulkActionTypes.ADD_TO_CAMPAIGN.id && (
				<BulkCampaignDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{selectedBulkActionId === BulkActionTypes.EXPORT_TO_CSV.id && (
				<BulkExportCSVDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{selectedBulkActionId === BulkActionTypes.ADD_TO_MARKET.id && (
				<BulkAddToMarketDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			{user && user.permissions.broker && user.admin && selectedBulkActionId === BulkActionTypes.ASSIGN_TO_AGENT.id && (
				<BulkAssignToAgentDialog
					onClose={handleBulkActionDialogClose}
					leadIds={Array.from(selectedLeadIds)}
				/>
			)}
			<BulkDeleteDialog
				open={bulkDeleteDialogOpen}
				onClose={() => setBulkDeleteDialogOpen(false)}
				onDeleted={() => setSelectedLeadIds(new Set())}
				leadIds={Array.from(selectedLeadIds)}
			/>
			<Mui.Grid container direction="column" spacing={3}>
				<Mui.Grid item className={classes.headerRow}>
					<Mui.Grid
						container
						alignItems="center"
						spacing={1}
						wrap="wrap"
					>
						<Mui.Grid item className={classes.searchItem}>
							<Mui.InputBase
								placeholder="Search Leads"
								defaultValue={initialTerm}
								onChange={onTermChange}
								startAdornment={
									<Mui.InputAdornment position="start">
										<FeatherIcon className={classes.searchIcon}>
											<Icons.Search />
										</FeatherIcon>
									</Mui.InputAdornment>
								}
								className={classNames(
									classes.searchInput,
									isMobile && classes.searchInputMobile
								)}
							/>
						</Mui.Grid>
						{user.permissions.crm && (
							<Mui.Grid item>
								<Mui.Button
									variant="outlined"
									onClick={showLeadFilterDialog}
									aria-label={isMobile ? "Filters" : undefined}
									className={classNames(
										classes.headerButton,
										classes.outlinedButton,
										isMobile && classes.iconOnlyButton
									)}
								>
									{isMobile ? (
										<FeatherIcon className={classes.buttonIcon}>
											<Icons.Filter />
										</FeatherIcon>
									) : (
										`Filters ${filterCount ? `(${filterCount})` : ""}`
									)}
								</Mui.Button>
							</Mui.Grid>
						)}
						{!isMobile && user.permissions.crm && (
							<Mui.Grid item>
								<Mui.Button
									variant="outlined"
									onClick={handleColumnsButtonClick}
									className={classNames(classes.headerButton, classes.outlinedButton)}
									endIcon={
										<FeatherIcon className={classes.buttonIcon}>
											<Icons.ChevronDown />
										</FeatherIcon>
									}
								>
									Columns
								</Mui.Button>
								<Mui.Menu
									anchorEl={columnsAnchorEl}
									open={Boolean(columnsAnchorEl)}
									onClose={handleColumnsMenuClose}
									PaperProps={{
										className: classes.menuPaper,
									}}
								>
									{columnOptions.map((type) => (
										<Mui.MenuItem
											key={type.id}
											onClick={() => handleColumnToggle(type)}
										>
											<Mui.Checkbox
												checked={columnTypes.includes(type)}
												color="primary"
											/>
											<Mui.ListItemText
												primary={type === LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON ? (
													<Mui.Box component="span" display="inline-flex" alignItems="center" gap={1}>
														{type.label}
														<SparklesIcon />
													</Mui.Box>
												) : type.label}
											/>
										</Mui.MenuItem>
									))}
								</Mui.Menu>
							</Mui.Grid>
						)}
						{!user.permissions.crm && !isMobile && (
							<Mui.Grid item>
								<Select
									multiple
									placeholder="Lead Status"
									value={filters.status}
									options={LeadStatus.values()}
									valueExtractor={(type) => type.id}
									labelExtractor={(type) => type.label + " Leads"}
									optionRenderer={(type) => <Mui.ListItemText primary={type.label} />}
									onChange={handleStatusChange}
									variant="outlined"
									className={classes.leadStatusSelect}
								/>
							</Mui.Grid>
						)}
						{!isMobile && <Mui.Grid item className={classes.headerSpacer} />}
						{!isMobile && !!selectedLeadIds.size && (
						<Mui.Grid item>
							<Mui.IconButton size="small" className={classes.deleteIconButton} onClick={() => setBulkDeleteDialogOpen(true)}>
								<FeatherIcon>
									<Icons.Trash />
								</FeatherIcon>
							</Mui.IconButton>
						</Mui.Grid>
					)}
					{!isMobile && canShowBulkActions && (
							<Mui.Grid item>
								<Mui.Button
									variant="text"
									disabled={!selectedLeadIds.size}
									onClick={handleBulkActionsButtonClick}
									className={classNames(
										classes.textButton,
										selectedLeadIds.size
											? classes.textButtonEnabled
											: classes.textButtonDisabled
									)}
									endIcon={
										<FeatherIcon
											className={
												selectedLeadIds.size
													? classes.buttonIcon
													: classes.buttonIconDisabled
											}
										>
											<Icons.ChevronDown />
										</FeatherIcon>
									}
								>
									Bulk Actions
								</Mui.Button>
								<Mui.Menu
									anchorEl={bulkActionsAnchorEl}
									open={Boolean(bulkActionsAnchorEl)}
									onClose={handleBulkActionsMenuClose}
									PaperProps={{
										className: classes.menuPaper,
									}}
								>
									{bulkActions.map((action) => (
										<Mui.MenuItem
											key={action.id}
											onClick={() => handleBulkActionSelect(action.id)}
										>
											<Mui.ListItemText primary={action.label} />
										</Mui.MenuItem>
									))}
								</Mui.Menu>
							</Mui.Grid>
						)}
						<Mui.Grid item>
							<Mui.Button
								variant="outlined"
								onClick={handleImportLeadsClick}
								disabled={isImporting}
								aria-label={isMobile ? "Import Leads" : undefined}
								className={classNames(
									classes.headerButton,
									classes.outlinedButton,
									isMobile && classes.iconOnlyButton,
									isImporting && classes.outlinedButtonDisabled
								)}
								>
								{isMobile ? (
									<FeatherIcon
										className={isImporting ? classes.buttonIconMuted : classes.buttonIcon}
									>
										<Icons.Upload />
									</FeatherIcon>
								) : isImporting ? (
									<Mui.Grid container alignItems="center" spacing={1}>
										<Mui.Grid item>
											<Mui.CircularProgress size={16} />
										</Mui.Grid>
										<Mui.Grid item>Importing...</Mui.Grid>
									</Mui.Grid>
								) : (
									"Import Leads"
								)}
							</Mui.Button>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button
								variant="contained"
								onClick={handleAddLeadDialogOpen}
								aria-label={isMobile ? "Add Lead" : undefined}
								className={classNames(
									classes.headerButton,
									classes.containedPrimaryButton,
									isMobile && classes.iconOnlyButton
								)}
							>
								{isMobile ? (
									<FeatherIcon className={classes.buttonIconOnPrimary}>
										<Icons.Plus />
									</FeatherIcon>
								) : (
									"Add Lead"
								)}
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item className={!isMobile ? classes.tableContainer : ""}>
					{importProgress?.inProgress && importProgress.total > 0 && (
						<Mui.Grid item className={classes.bannerWrapper}>
							<MuiAlert
								severity="info"
								className={classes.infoAlert}
								action={
									<Mui.Button
										color="inherit"
										size="small"
										onClick={handleStopImport}
										disabled={isStopping}
										startIcon={isStopping ? <Mui.CircularProgress size={16} color="inherit" /> : undefined}
									>
										{isStopping ? "Stopping... finishing current batch (up to 25)" : "Stop"}
									</Mui.Button>
								}
							>
								<Mui.Grid container direction="column" className={classes.progressAlertContent}>
									<Mui.Grid item>
										{`Importing leads: ${importProgress.processed} of ${importProgress.total}`}
									</Mui.Grid>
									<Mui.Grid item className={classes.progressBarContainer}>
										<Mui.LinearProgress
											color="secondary"
											variant="determinate"
											value={(importProgress.processed * 100) / importProgress.total}
											className={classes.progressBar}
										/>
									</Mui.Grid>
									{isStopping && (
										<Mui.Grid item>
											<Mui.Typography variant="caption" style={{ marginTop: 4 }}>
												Stopping after the current batch (up to 25 leads) finishes.
											</Mui.Typography>
										</Mui.Grid>
									)}
									<Mui.Grid item>
										<Mui.Typography variant="caption" style={{ marginTop: 4 }}>
											You can continue using the app while importing. Refreshing will stop the import, but already imported leads are saved.
										</Mui.Typography>
									</Mui.Grid>
								</Mui.Grid>
							</MuiAlert>
						</Mui.Grid>
					)}
					{recentImport && !recentImport.dismissed && recentImport.emails.length > 0 && recentImport.completed && (
						<Mui.Grid item className={classes.bannerWrapper}>
							<MuiAlert
								severity="success"
								icon={
									<FeatherIcon>
										<Icons.CheckCircle />
									</FeatherIcon>
								}
								className={classes.successAlert}
								action={
									<Mui.Button
										color="inherit"
										size="small"
										onClick={handleDismissRecentImport}
									>
										Dismiss
									</Mui.Button>
								}
							>
								{`Successfully imported ${recentImportCount} lead${recentImportCount === 1 ? "" : "s"}. New entries are highlighted (up to 500).`}
							</MuiAlert>
						</Mui.Grid>
					)}
					<StopImportConfirmDialog
						open={stopConfirmOpen}
						onCancel={handleCancelStop}
						onConfirm={handleConfirmStop}
					/>
					{loading && (
						<Mui.Grid item className={classes.tableLoading}>
							<Mui.CircularProgress size={24} />
						</Mui.Grid>
					)}
					{isMobile ? (
						<Table
							items={sortedAllActiveLeads}
							rowClassName={(lead) =>
								isRecentlyImported(lead) ? classes.recentlyImportedRow : undefined
							}
							columns={[
								{
									id: "avatar",
									hidden: !columnTypes.includes(LeadColumnType.AVATAR),
									width: 70,
									value: (lead) => (
										<div className={classes.centeredCell}>
											<LeadAvatar lead={lead} />
										</div>
									),
								},
								{
									id: "label",
									hidden: !columnTypes.includes(LeadColumnType.LABEL),
									title: LeadColumnType.LABEL.label,
									sort: (lead1, lead2) =>
										lead1.label.toLowerCase() < lead2.label.toLowerCase() ? -1 : 1,
									value: (lead) => {
										const url = urls.lead(lead);
										return (
											<Mui.Grid container direction="column">
												<Mui.Link component={RouterLinkWrapper} to={url}>
													{lead.label}
												</Mui.Link>
												{lead.phone && (
													<Mui.Typography className={classes.phoneText}>
														{formatPhoneNumber(lead.phone)}
													</Mui.Typography>
												)}
											</Mui.Grid>
										);
									},
								},
							]}
							pageSize={pageSize}
							onPageSizeChange={handlePageSizeChange}
							page={page}
							onPageChange={handlePageChange}
							sort={sort}
							onSortChange={handleSortChange}
						/>
					) : (
						<Table
							items={sortedLeads}
							fixedHeight={330 + bannerOffset}
							rowClassName={(lead) =>
								classNames(
									isRecentlyImported(lead) && classes.recentlyImportedRow,
									lead.id === focusLeadId && classes.focusedLeadRow
								) || undefined
							}
							columns={[
								{
									id: "avatar",
									hidden: !columnTypes.includes(LeadColumnType.AVATAR),
									width: 70,
									value: (lead) => (
										<div className={classes.centeredCell}>
											<LeadAvatar lead={lead} />
										</div>
									),
								},
								{
									id: "label",
									hidden: !columnTypes.includes(LeadColumnType.LABEL),
									title: LeadColumnType.LABEL.label,
									sort: leadSortColumns.label.compare,
									value: (lead) => {
										const url = urls.lead(lead);
										return (
											<Mui.Grid container direction="column">
												<Mui.Link
													component={RouterLinkWrapper}
													to={url}
													onClick={(event: React.MouseEvent) => handleLeadClick(event, lead)}
												>
													{lead.label}
												</Mui.Link>
												{lead.phone && (
													<Mui.Typography className={classes.phoneText}>
														{formatPhoneNumber(lead.phone)}
													</Mui.Typography>
												)}
											</Mui.Grid>
										);
									},
								},
								{
									id: "bookmarked",
									hidden:
										!user.permissions.crmTags ||
										!columnTypes.includes(LeadColumnType.BOOKMARKED),
									width: 70,
									value: (lead) => <Bookmark leadId={lead.id} />,
								},
								{
									id: "status",
									hidden: !columnTypes.includes(LeadColumnType.STATUS),
									title: LeadColumnType.STATUS.label,
									width: 70,
									sort: leadSortColumns.status.compare,
									value: (lead) => lead.status.label,
								},
								{
									id: "pipeline",
									hidden:
										!user.permissions.crmPipeline ||
										!columnTypes.includes(LeadColumnType.PIPELINE),
									title: LeadColumnType.PIPELINE.label,
									sort: leadSortColumns.pipeline.compare,
									value: (lead) => (lead.pipeline ? lead.pipeline.type.label : null),
								},
								{
									id: "type",
									hidden:
										!user.permissions.crmTags ||
										!columnTypes.includes(LeadColumnType.TYPE),
									title: LeadColumnType.TYPE.label,
									value: (lead) => <Type leadId={lead.id} />,
								},
								{
									id: "createdOn",
									hidden: !columnTypes.includes(LeadColumnType.CREATED_ON),
									title: LeadColumnType.CREATED_ON.label,
									sort: leadSortColumns.createdOn.compare,
									value: (lead) => numberedDateFormatter(lead.createdOn),
								},
								{
									id: "lastActiveOn",
									hidden:
										!user.permissions.leadLastActive ||
										!columnTypes.includes(LeadColumnType.LAST_ACTIVE_ON),
									title: LeadColumnType.LAST_ACTIVE_ON.label,
									sort: leadSortColumns.lastActiveOn.compare,
									value: (lead) =>
										lead.lastActiveOn ? numberedDateFormatter(lead.lastActiveOn) : null,
								},
								{
									id: "lastSearchAgentChatOn",
									hidden:
										!hasSearchAgent(user) ||
										!columnTypes.includes(LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON),
									title: LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON.label,
									sort: leadSortColumns.lastSearchAgentChatOn.compare,
									isBlank: leadSortColumns.lastSearchAgentChatOn.isBlank,
									value: (lead) =>
										lead.lastSearchAgentChatOn ? numberedDateFormatter(lead.lastSearchAgentChatOn) : null,
								},
								{
									id: "subscribed",
									hidden: !columnTypes.includes(LeadColumnType.SUBSCRIBED),
									title: LeadColumnType.SUBSCRIBED.label,
									width: 100,
									sort: leadSortColumns.subscribed.compare,
									value: (lead) =>
										lead.subscribed ? (
											<FeatherIcon>
												<Icons.Check />
											</FeatherIcon>
										) : null,
								},
								{
									id: "rating",
									hidden: !columnTypes.includes(LeadColumnType.RANKING),
									title: LeadColumnType.RANKING.label,
									sort: leadSortColumns.rating.compare,
									value: (lead) => (
										<Ranking
											leadId={lead.id}
											sortedLeadsByRanking={leadRankData.sortedLeads}
											ratingSize={leadRankData.ratingSize}
										/>
									),
								},
								{
									id: "source",
									hidden: !columnTypes.includes(LeadColumnType.SOURCE),
									title: LeadColumnType.SOURCE.label,
									sort: leadSortColumns.source.compare,
									value: (lead) => lead.source,
								},
								{
									id: "agent",
									hidden:
										!(user.permissions.broker && user.permissions.showTeamFeatures) ||
										!columnTypes.includes(LeadColumnType.AGENT),
									title: LeadColumnType.AGENT.label,
									sort: leadSortColumns.agent.compare,
									value: (lead) => {
										const { agent } = lead;
										if (!agent) return null;
										return (
											<Mui.Link
												component={RouterLinkWrapper}
												to={urls.settingsAgent(agent)}
											>
												{agent.label}
											</Mui.Link>
										);
									},
								},
								{
									id: "tags",
									hidden:
										!user.permissions.crmTags ||
										!columnTypes.includes(LeadColumnType.TAGS),
									title: LeadColumnType.TAGS.label,
									getProps: () => ({
										style: {
											flexFlow: "wrap",
											justifyContent: "flex-start",
											alignItems: "center",
										},
									}),
									value: (lead) => <Tag leadId={lead.id} />,
								},
								{
									id: "timeline",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.TIMELINE),
									title: LeadColumnType.TIMELINE.label,
									value: (lead) =>
										lead.justBrowsing
											? "Just Browsing"
											: lead.transactionTimelineStart && lead.transactionTimelineEnd
											? `${shortDateFormatter(
													moment(lead.transactionTimelineStart)
											  )} - ${shortDateFormatter(moment(lead.transactionTimelineEnd))}`
											: "",
								},
								{
									id: "readiness",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.READINESS),
									title: LeadColumnType.READINESS.label,
									value: (lead) =>
										lead.readiness ? LeadReadiness.getById(lead.readiness)?.label : "",
								},
								{
									id: "mortgagePreApproval",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.MORTGAGE_PRE_APPROVAL),
									title: LeadColumnType.MORTGAGE_PRE_APPROVAL.label,
									value: (lead) =>
										lead.mortgagePreApproval !== undefined
											? YesNoType.getByValue(lead.mortgagePreApproval)?.label
											: "",
								},
								{
									id: "transactionPrice",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.TRANSACTION_PRICE),
									title: LeadColumnType.TRANSACTION_PRICE.label,
									value: (lead) =>
										lead.transactionPrice ? formatPriceInput(lead.transactionPrice) : "",
								},
								{
									id: "workingWithAnotherAgent",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.WORKING_WITH_ANOTHER_AGENT),
									title: LeadColumnType.WORKING_WITH_ANOTHER_AGENT.label,
									value: (lead) =>
										lead.workingWithAnotherAgent !== undefined
											? YesNoType.getByValue(lead.workingWithAnotherAgent)?.label
											: "",
								},
								{
									id: "referralRequested",
									hidden:
										!user.permissions.extendedLeadFields ||
										!columnTypes.includes(LeadColumnType.REFERRAL_REQUESTED),
									title: LeadColumnType.REFERRAL_REQUESTED.label,
									value: (lead) => (lead.referralRequested ? "Yes" : "No"),
								},
								{
									id: "delete",
									width: 70,
									showOnHover: true,
									value: (lead) => (
										<Mui.IconButton onClick={() => handleShowDeleteLeadDialog(lead)}>
											<FeatherIcon>
												<Icons.Trash />
											</FeatherIcon>
										</Mui.IconButton>
									),
								},
							]}
							pageSize={pageSize}
							onPageSizeChange={handlePageSizeChange}
							page={page}
							onPageChange={handlePageChange}
							sort={sort}
							onSortChange={handleSortChange}
							onSelect={handleSelect}
							onUnselect={handleUnselect}
							isSelected={(lead) => selectedLeadIds.has(lead.id)}
						/>
					)}
				</Mui.Grid>
			</Mui.Grid>
		</>
	);
};

export const LeadTable = withMediaQuery("(max-width:600px)")(
	withStyles(connect(mapStateToProps, mapDispatchToProps)(LeadTableComponent), styles)
);
